// src/contexts/LeaveContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import leaveService from '../services/leave.service';
import { sessionCache } from '../utils/sessionCache';
import { getCurrentISTDate } from '../utils/dateHelpers';

const LeaveContext = createContext();

export const useLeave = () => {
    const context = useContext(LeaveContext);
    if (!context) {
        throw new Error('useLeave must be used within LeaveProvider');
    }
    return context;
};

export const LeaveProvider = ({ children }) => {
    const { user } = useAuth();
    const [leaveBalances, setLeaveBalances] = useState([]);
    const [leaveHistory, setLeaveHistory] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [rawLeaveBalance, setRawLeaveBalance] = useState(null);
    const [rawLeaveHistory, setRawLeaveHistory] = useState([]);
    const [leaveStats, setLeaveStats] = useState({
        weeklyPattern: [],
        consumedTypes: [],
        monthlyStats: []
    });
    const [loading, setLoading] = useState(false);

    // Initialize leave data when user changes
    useEffect(() => {
        if (user?.id) {
            loadLeaveData(user.id);
        }
    }, [user?.id]);

    // Fetch leave data from backend
    const fetchLeaveData = useCallback(async (forceRefresh = false) => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            //console.log('⚠️ No auth token, cannot fetch leave data');
            return;
        }

        // Check cache first if not forcing refresh
        if (!forceRefresh) {
            const cachedBalance = sessionCache.get('leave_balance');
            const cachedHistory = sessionCache.get('leave_history');
            const cachedPending = sessionCache.get('pending_requests');

            if (cachedBalance && cachedHistory) {
                //console.log('📦 Using cached leave data');
                setLeaveBalances(cachedBalance);
                setLeaveHistory(cachedHistory);
                setPendingRequests(cachedPending || []);
                calculateLeaveStats(cachedHistory);
                return;
            }
        }

        try {
            setLoading(true);

            // Fetch leave balance
            const balanceData = await leaveService.getLeaveBalance();
            console.log('📊 Leave balance API response:', balanceData);
            setRawLeaveBalance(balanceData);
            let mappedBalances = [];
            if (balanceData.balances) {
                mappedBalances = balanceData.balances.map(bal => ({
                    id: bal.id,
                    type: bal.leave_type_name || bal.type,
                    available: bal.available || 0,
                    consumed: bal.consumed || 0,
                    accruedSoFar: bal.accrued || 0,
                    annualQuota: bal.annual_quota || 0,
                    color: getLeaveColor(bal.leave_type_name || bal.type),
                    employeeId: user?.id
                }));
                setLeaveBalances(mappedBalances);
            }

            // Fetch leave history
            const historyData = await leaveService.getLeaveHistory();
            setRawLeaveHistory(historyData);
            let mappedHistory = [];
            let pendingLeaves = [];
            if (Array.isArray(historyData)) {
                mappedHistory = historyData.map(leave => ({
                    id: leave.id,
                    leaveType: leave.leave_type_name || leave.leaveType,
                    fromDate: leave.start_date?.split('T')[0] || leave.fromDate,
                    toDate: leave.end_date?.split('T')[0] || leave.toDate,
                    days: leave.total_days || leave.days,
                    status: leave.status || 'Pending',
                    appliedOn: leave.applied_date?.split('T')[0] || leave.appliedOn,
                    reason: leave.reason || '',
                    employeeId: user?.id
                }));

                // Filter pending requests from history
                pendingLeaves = mappedHistory.filter(leave =>
                    leave.status && leave.status.toLowerCase() === 'pending'
                );

                setLeaveHistory(mappedHistory);
                setPendingRequests(pendingLeaves);
                calculateLeaveStats(mappedHistory);
            }

            // Cache the data
            sessionCache.set('leave_balance', mappedBalances);
            sessionCache.set('leave_history', mappedHistory);
            sessionCache.set('pending_requests', pendingLeaves);
            //console.log('💾 Cached leave data');

        } catch (error) {
            console.error('Error fetching leave data:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    const loadLeaveData = async (employeeId) => {
        // Try to fetch from backend first
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
            await fetchLeaveData();
        } else {
            // Fallback to empty state when no auth token
            //console.log('⚠️ No auth token, leave data will be empty');
            setLeaveBalances([]);
            setLeaveHistory([]);
            setPendingRequests([]);
            setRawLeaveBalance(null);
            setRawLeaveHistory([]);
            setLeaveStats({
                weeklyPattern: [],
                consumedTypes: [],
                monthlyStats: []
            });
        }
    };

    const calculateLeaveStats = (history) => {
        // Weekly pattern - count leaves per day of week
        const weeklyPattern = [
            { day: 'M', count: 0 },
            { day: 'T', count: 0 },
            { day: 'W', count: 1 },
            { day: 'T', count: 0 },
            { day: 'F', count: 2 },
            { day: 'S', count: 0 },
            { day: 'S', count: 0 }
        ];

        // Consumed leave types
        const consumedTypes = history.reduce((acc, leave) => {
            const existing = acc.find(item => item.type === leave.leaveType);
            if (existing) {
                existing.days += leave.days;
            } else {
                acc.push({ type: leave.leaveType, days: leave.days, color: getLeaveColor(leave.leaveType) });
            }
            return acc;
        }, []);

        // Monthly stats - leaves per month
        const monthlyStats = [
            { month: 'JAN', count: 0 },
            { month: 'FEB', count: 0 },
            { month: 'MAR', count: 0 },
            { month: 'APR', count: 0 },
            { month: 'MAY', count: 0 },
            { month: 'JUN', count: 0 },
            { month: 'JUL', count: 1 },
            { month: 'AUG', count: 4 },
            { month: 'SEP', count: 2 },
            { month: 'OCT', count: 0 },
            { month: 'NOV', count: 0 },
            { month: 'DEC', count: 0 }
        ];

        setLeaveStats({ weeklyPattern, consumedTypes, monthlyStats });
    };

    const getLeaveColor = (type) => {
        const colors = {
            'Casual Leave': 'purple',
            'Paid Leave': 'yellow',
            'Sick Leave': 'blue'
        };
        return colors[type] || 'gray';
    };

    const requestLeave = async (leaveData) => {
        const authToken = localStorage.getItem('authToken');

        console.log('🔍 LeaveContext.requestLeave called with data:', leaveData);
        console.log('🔍 Available leave balances:', rawLeaveBalance);

        // Try backend API first
        if (authToken) {
            try {
                // Get the leave type name from the leave balance data
                // The leave types are passed as availableLeaveTypes to LeaveRequestModal
                // We need to find the leave type object that matches the selected leaveTypeId
                const leaveBalances = rawLeaveBalance?.balances || rawLeaveBalance || [];
                const leaveBalanceArray = Array.isArray(leaveBalances) ? leaveBalances : [];
                const selectedLeaveType = leaveBalanceArray.find(lt =>
                    lt.leave_type_id === leaveData.leaveTypeId ||
                    lt.id === leaveData.leaveTypeId
                );

                console.log('✅ Selected leave type:', selectedLeaveType);
                console.log('📋 Form leave data fields:', {
                    leaveTypeId: leaveData.leaveTypeId,
                    fromDate: leaveData.fromDate,
                    toDate: leaveData.toDate,
                    days: leaveData.days,
                    reason: leaveData.reason
                });

                const apiPayload = {
                    leaveTypeId: leaveData.leaveTypeId,
                    fromDate: leaveData.fromDate,
                    toDate: leaveData.toDate,
                    reason: leaveData.reason,
                    emergencyContact: leaveData.emergencyContact
                };

                console.log('🚀 API payload being sent:', apiPayload);

                const response = await leaveService.applyLeave(apiPayload);

                console.log('📋 API response:', response);

                // Refresh leave data after successful application
                await fetchLeaveData();

                return { success: true, message: 'Leave request submitted successfully' };
            } catch (error) {
                console.error('❌ Backend leave application failed:', error);
                console.error('❌ Error response:', error.response?.data);

                // Extract error message from backend response
                const errorMessage = error.response?.data?.error ||
                                   error.response?.data?.message ||
                                   error.message ||
                                   'Failed to submit leave request';

                return { success: false, message: errorMessage };
            }
        }

        // Fallback to localStorage
        try {
            const newRequest = {
                id: Date.now(),
                ...leaveData,
                employeeId: user.id,
                status: 'Pending',
                appliedOn: getCurrentISTDate()
            };

            setPendingRequests(prev => [...prev, newRequest]);
            setLeaveHistory(prev => [...prev, newRequest]);

            return { success: true, message: 'Leave request submitted successfully' };
        } catch (error) {
            return { success: false, message: 'Failed to submit leave request' };
        }
    };

    const awardCompensatoryOff = async (compOffData) => {
        const authToken = localStorage.getItem('authToken');

        // Try backend API first
        if (authToken) {
            try {
                const response = await leaveService.awardCompensatoryOff(compOffData);

                // Refresh leave data after awarding comp-off
                await fetchLeaveData(true); // Force refresh

                return {
                    success: true,
                    message: response.message || 'Compensatory off awarded successfully'
                };
            } catch (error) {
                console.error('Award compensatory off error:', error);
                const errorMessage = error.response?.data?.error || error.message || 'Failed to award compensatory off';
                return { success: false, message: errorMessage };
            }
        }

        // Fallback to simulated behavior (should not be used in production)
        try {
            const compOff = {
                id: Date.now(),
                type: 'Compensatory Off',
                ...compOffData,
                employeeId: user.id,
                available: compOffData.days,
                consumed: 0,
                accruedSoFar: compOffData.days,
                annualQuota: compOffData.days,
                color: 'green'
            };

            setLeaveBalances(prev => [...prev, compOff]);

            return { success: true, message: 'Compensatory off awarded successfully' };
        } catch (error) {
            return { success: false, message: 'Failed to award compensatory off' };
        }
    };

    const cancelLeave = async (leaveId) => {
        const authToken = localStorage.getItem('authToken');

        // Try backend API first
        if (authToken) {
            try {
                await leaveService.cancelLeave(leaveId);

                // Refresh leave data after successful cancellation
                await fetchLeaveData();

                return { success: true, message: 'Leave cancelled successfully' };
            } catch (error) {
                console.error('Backend leave cancellation failed:', error);
                return { success: false, message: 'Failed to cancel leave' };
            }
        }

        // Fallback to localStorage
        try {
            setPendingRequests(prev => prev.filter(req => req.id !== leaveId));
            setLeaveHistory(prev => prev.map(leave =>
                leave.id === leaveId ? { ...leave, status: 'Cancelled' } : leave
            ));
            return { success: true, message: 'Leave cancelled successfully' };
        } catch (error) {
            return { success: false, message: 'Failed to cancel leave' };
        }
    };

    const getLeaveBalancesByPeriod = (startDate, endDate) => {
        // Filter and calculate balances for specific period
        return leaveBalances;
    };

    const getLeaveHistoryByPeriod = (startDate, endDate) => {
        return leaveHistory.filter(leave => {
            const leaveDate = new Date(leave.fromDate);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return leaveDate >= start && leaveDate <= end;
        });
    };

    const value = {
        leaveBalances,
        leaveHistory,
        pendingRequests,
        rawLeaveBalance,
        rawLeaveHistory,
        leaveStats,
        loading,
        fetchLeaveData,
        requestLeave,
        awardCompensatoryOff,
        cancelLeave,
        getLeaveBalancesByPeriod,
        getLeaveHistoryByPeriod
    };

    return (
        <LeaveContext.Provider value={value}>
            {children}
        </LeaveContext.Provider>
    );
};
